import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { fmtTime12 } from '../../utils/time';

const fmtDT = v => { try { return format(parseISO(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };
const fmtDate = v => { try { return format(parseISO(v), 'd MMM yyyy'); } catch { return v || '–'; } };
const varCell = v => {
  if (v == null) return <span className="text-slate-400">–</span>;
  const abs = Math.abs(v);
  return (
    <span className={clsx('font-medium text-xs', abs === 0 ? 'text-green-600' : abs >= 30 ? 'text-red-600' : 'text-amber-600')}>
      {v > 0 ? '+' : ''}{v} min
    </span>
  );
};
// clock_*_verified DEFAULT false, so a clock with no GPS fix reads false too — judge "no GPS" from
// the distance and show it neutral grey (never as Unverified). Not clocked at all → '–'.
const geoVerified = (timeKey, distKey) => (v, row) => {
  if (!row?.[timeKey]) return <span className="text-slate-400">–</span>;
  if (v) return <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Verified</span>;
  if (row[distKey] == null) return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">No GPS</span>;
  return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">Unverified</span>;
};
const geoDist = (timeKey) => (v, row) => {
  if (v != null) return `${v}m`;
  return row?.[timeKey] ? <span className="text-slate-400 text-xs">No GPS</span> : '–';
};

const statusBadge = v => {
  const map = { approved:'bg-green-100 text-green-700', pending:'bg-amber-100 text-amber-700', rejected:'bg-red-100 text-red-700' };
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', map[v] || 'bg-slate-100 text-slate-600')}>{v || '–'}</span>;
};

const COLS = [
  { key: 'shift_date',            label: 'Shift Date',         render: fmtDate },
  { key: 'staff_name',            label: 'Staff Name' },
  { key: 'client_names',          label: 'Client(s)' },
  { key: 'shift_type',            label: 'Shift Type' },
  { key: 'scheduled_start',       label: 'Sched. Start',       render: fmtTime12 },
  { key: 'scheduled_end',         label: 'Sched. End',         render: fmtTime12 },
  { key: 'clock_in_time',         label: 'Clock In',           render: fmtDT },
  { key: 'clock_in_variance_min', label: 'In Variance',        render: varCell },
  { key: 'clock_in_verified',     label: 'In Verified',        render: geoVerified('clock_in_time', 'clock_in_distance_m') },
  { key: 'clock_in_distance_m',   label: 'In Distance (m)',    render: geoDist('clock_in_time') },
  { key: 'clock_out_time',        label: 'Clock Out',          render: fmtDT },
  { key: 'clock_out_variance_min',label: 'Out Variance',       render: varCell },
  { key: 'clock_out_verified',    label: 'Out Verified',       render: geoVerified('clock_out_time', 'clock_out_distance_m') },
  { key: 'clock_out_distance_m',  label: 'Out Distance (m)',   render: geoDist('clock_out_time') },
  { key: 'hours_worked',          label: 'Hours Worked',       render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'timesheet_status',      label: 'Status',             render: statusBadge },
  { key: 'clock_in_reason',       label: 'Reason' },
];

export default function ClockInOutReport() {
  const [data, setData]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const [status, setStatus]     = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId) p.set('staff_id', staffId);
    if (status)  p.set('status', status);
    api.get(`/reports/clock-in-out?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, status]);

  useEffect(() => { load(); }, [load]);

  const selStaff = staff.find(s => s.id === staffId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff ? [{ label: 'Staff', value: `${selStaff.first_name} ${selStaff.last_name}`, onRemove: () => setStaffId('') }] : []),
    ...(status ? [{ label: 'Status', value: status, onRemove: () => setStatus('') }] : []),
  ];

  return (
    <ReportLayout title="Clock In and Out Report" data={data} filename="clock_in_out_report" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
